import { useEffect, useState } from "react";
import { PhoneOff, MailX, Ghost, ShieldOff, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

const VILLAINS = [
  { key: "spam_calls",        icon: PhoneOff,  label: "Spam callers",       tag: "PHONE",   accent: "text-rose-400 border-rose-500/30 bg-rose-500/5" },
  { key: "disposable_emails", icon: MailX,     label: "Disposable inboxes", tag: "EMAIL",   accent: "text-amber-400 border-amber-500/30 bg-amber-500/5" },
  { key: "tor_exits",         icon: Ghost,     label: "Tor exit nodes",     tag: "IP",      accent: "text-purple-400 border-purple-500/30 bg-purple-500/5" },
  { key: "vpn_proxies",       icon: EyeOff,    label: "Sneaky VPN proxies", tag: "IP",      accent: "text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-500/5" },
  { key: "ported_scams",      icon: ShieldOff, label: "Port-out scams",     tag: "PHONE",   accent: "text-orange-400 border-orange-500/30 bg-orange-500/5" },
];

function Counter({ value }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    if (display === value) return;
    const diff = value - display;
    const steps = Math.min(24, Math.max(6, Math.abs(diff)));
    const step = diff / steps;
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplay((d) => Math.round(d + step));
      if (i >= steps) { clearInterval(id); setDisplay(value); }
    }, 40);
    return () => clearInterval(id);
  }, [value]);
  return <span className="font-display font-bold tracking-tight tabular-nums">{Math.max(0, display).toLocaleString()}</span>;
}

export default function VillainWall() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const { data } = await api.get("/threats/counters");
        if (!cancelled) setCounts(data.counts || {});
      } catch {}
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="villain-wall">
      <div className="max-w-2xl mb-10">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-rose-500/30 bg-rose-500/5 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 pulse-dot" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-rose-400">Live · updated every 4s</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">
          Meet the bad ones.
          <span className="block text-slate-500 font-medium mt-2 text-2xl sm:text-3xl">We block them so your users don't have to.</span>
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {VILLAINS.map((v) => {
          const Icon = v.icon;
          return (
            <div key={v.key} data-testid={`villain-card-${v.key}`} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover-lift hover:border-cyan-500/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid opacity-[0.06] pointer-events-none" />
              <div className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest border ${v.accent}`}>{v.tag}</div>
              <div className="mt-4 flex items-baseline gap-1.5">
                <Icon className="w-5 h-5 text-slate-500 group-hover:text-rose-400 transition-colors" strokeWidth={2} />
              </div>
              <div className="mt-3 text-3xl sm:text-4xl text-slate-100">
                <Counter value={counts[v.key] || 0} />
              </div>
              <div className="mt-1 text-xs text-slate-400 leading-snug">{v.label} blocked</div>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-slate-600">last 24h</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
